import Honeybadger from '@honeybadger-io/js'
import { shouldReportErrors } from '~/cynqueModules/shouldReportErrors'

// Configure the browser Honeybadger instance early (01. prefix) so client errors
// in later plugins/pages are captured. Runs before MSW (02.) and the rest.
export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig()
  Honeybadger.configure({
    apiKey: config.public.honeybadgerApiKey,
    environment: config.public.releaseStage,
    // The half of source-map pairing that lives in the browser. Honeybadger
    // matches an uploaded map to an error by REVISION, so a notice without one
    // can never be de-minified no matter how correct the upload is — this was
    // commented out, and production backtraces arrived as `_nuxt/BGRlVu6Z.js:2`
    // with `application_trace: 0 frames`. Same build-time constant the upload
    // hook uses (RELEASE_REVISION in nuxt.config.ts); the two must not drift.
    revision: config.public.honeybadgerRevision,
    // Keep local dev and the headless e2e runs OUT of the real project. They
    // carry a live API key because bin/launch-app sources the staging Heroku
    // config vars — and calling themselves "staging" is exactly what defeats
    // Honeybadger's own developmentEnvironments guard, so they reported like
    // production. See the reasoning in shouldReportErrors.
    //
    // `false` disables sending only; notify() stays safe to call everywhere
    // (EntityDialog, services/users, cspReporter all call it directly), and the
    // client still logs a debug line for each suppressed report.
    reportData: shouldReportErrors({
      isDev: import.meta.dev,
      // Guarded: this plugin is not .client-suffixed, so it also runs where
      // there is no window.
      hostname: import.meta.client ? window.location.hostname : undefined,
    }),
    // Breadcrumbs — the trail of clicks, navigations, console lines and network
    // calls leading up to an error.
    //
    // ⚠ THIS IS ALREADY THE SDK DEFAULT (`breadcrumbsEnabled: true`,
    // `maxBreadcrumbs: 40` in @honeybadger-io/js 6.14.0). Stated explicitly
    // because their ABSENCE FROM THE REST API MADE THEM LOOK DISABLED: the
    // `/v2/projects/:id/faults/:id/notices` payload carries no `breadcrumbs`
    // key at all, and an earlier investigation read that as "not enabled in
    // plugins/01.honeybadger.ts" and logged it as work to do. They were never
    // off — they are simply not exposed there. Read them in the Honeybadger UI.
    //
    // Pinned rather than left implicit so an SDK default change cannot quietly
    // take them away from the error class that needs them most: a stackless
    // `window.onerror` (e.g. the ResizeObserver loop notice, fault 133422964),
    // where the trail is the only evidence that exists.
    breadcrumbsEnabled: true,
    maxBreadcrumbs: 40,
    // NOTE: `projectRoot` is deliberately NOT set. It was `'webpack:///./'`,
    // left from the webpack build that Vite replaced — Vite/Rollup never emits
    // `webpack:///` paths, so it stripped a prefix that no backtrace contained.
    // Honeybadger's browser default (the page origin) is what actually matches
    // the full asset URLs that appear in browser stack frames.
  })
})
